// ============================================================
// ByOurConsent.com -- Store Page
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import api    from '../utils/api';
import useWindowWidth from '../hooks/useWindowWidth';

// Product images can be either a local relative path (/uploads/...)
// or a full absolute URL (e.g. Printful's CDN) -- only prepend our
// backend origin for the former.
// Derives the backend origin from the same env var api.js uses, so
// production, dev, and any future domain/port all stay in sync from
// one place. Falls back to the documented dev port if unset.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5002/api').replace(/\/api\/?$/, '');
const imageUrl = (path) => path ? (path.startsWith('http') ? path : `${API_ORIGIN}${path}`) : path;

// ============================================================
// Product card
// ============================================================
const ProductCard = ({ product }) => {
    const [hovered, setHovered] = useState(false);

    const price = parseFloat(product.current_price || product.price);
    const hasDiscount = product.sale_price &&
        (!product.sale_ends_at || new Date(product.sale_ends_at) > new Date());

    return (
        <Link to={`/store/${product.slug}`} style={{ textDecoration: 'none' }}>
            <div
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background:   '#f5f0e8',
                    border:       `0.5px solid ${hovered ? '#8b1a1a' : '#c8beaa'}`,
                    borderRadius: '4px',
                    overflow:     'hidden',
                    cursor:       'pointer',
                    transition:   'border-color 0.15s, transform 0.15s',
                    transform:    hovered ? 'translateY(-2px)' : 'none'
                }}>

                {/* Product image */}
                <div style={{
                    background:     '#ede8dc',
                    height:         '240px',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    overflow:       'hidden',
                    position:       'relative'
                }}>
                    {product.primary_image_url ? (
                        <img
                            src={imageUrl(product.primary_image_url)}
                            alt={product.name}
                            style={{
                                width:      '100%',
                                height:     '100%',
                                objectFit:  'cover'
                            }}
                            onError={e => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                            }}
                        />
                    ) : null}
                    <div style={{
                        display:        product.primary_image_url ? 'none' : 'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        width:          '100%',
                        height:         '100%',
                        fontSize:       '13px',
                        fontFamily:     'sans-serif',
                        color:          '#8a8070'
                    }}>
                        By Our Consent
                    </div>

                    {/* Featured badge */}
                    {product.is_featured && (
                        <div style={{
                            position:      'absolute',
                            top:           '10px',
                            left:          '10px',
                            background:    '#8b1a1a',
                            color:         '#f5f0e8',
                            fontSize:      '9px',
                            fontFamily:    'sans-serif',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding:       '3px 8px',
                            borderRadius:  '2px'
                        }}>
                            Featured
                        </div>
                    )}

                    {/* Sale badge */}
                    {hasDiscount && (
                        <div style={{
                            position:      'absolute',
                            top:           '10px',
                            right:         '10px',
                            background:    '#3b6d11',
                            color:         '#f5f0e8',
                            fontSize:      '9px',
                            fontFamily:    'sans-serif',
                            letterSpacing: '0.12em',
                            textTransform: 'uppercase',
                            padding:       '3px 8px',
                            borderRadius:  '2px'
                        }}>
                            Sale
                        </div>
                    )}
                </div>

                {/* Product info */}
                <div style={{ padding: '14px' }}>
                    {/* Category */}
                    <div style={{
                        fontSize:      '9px',
                        fontFamily:    'sans-serif',
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color:         '#8b6a3a',
                        marginBottom:  '4px'
                    }}>
                        {product.category}
                    </div>

                    {/* Name */}
                    <div style={{
                        fontSize:     '15px',
                        fontWeight:   600,
                        color:        hovered ? '#8b1a1a' : '#1e2a3a',
                        fontFamily:   "'EB Garamond', Georgia, serif",
                        lineHeight:   1.2,
                        marginBottom: '8px',
                        transition:   'color 0.15s'
                    }}>
                        {product.name}
                    </div>

                    {/* Price + CTA */}
                    <div style={{
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'space-between'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{
                                fontSize:   '16px',
                                fontWeight: 600,
                                color:      '#1e2a3a',
                                fontFamily: "'EB Garamond', Georgia, serif"
                            }}>
                                ${price.toFixed(2)}
                            </span>
                            {hasDiscount && (
                                <span style={{
                                    fontSize:      '12px',
                                    fontFamily:    'sans-serif',
                                    color:         '#8a8070',
                                    textDecoration: 'line-through'
                                }}>
                                    ${parseFloat(product.price).toFixed(2)}
                                </span>
                            )}
                        </div>
                        <div style={{
                            fontSize:      '10px',
                            fontFamily:    'sans-serif',
                            color:         '#8b1a1a',
                            letterSpacing: '0.06em',
                            textTransform: 'uppercase'
                        }}>
                            {product.product_type === 'digital' ? 'Download →' : 'Select size →'}
                        </div>
                    </div>

                    {/* Variant count */}
                    {product.variant_count > 0 && (
                        <div style={{
                            fontSize:   '11px',
                            fontFamily: 'sans-serif',
                            color:      '#8a8070',
                            marginTop:  '6px'
                        }}>
                            {product.variant_count} sizes available
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
};

// ============================================================
// StorePage
// ============================================================
const StorePage = () => {
    const width    = useWindowWidth();
    const isMobile = width < 768;
    const [products,   setProducts]   = useState([]);
    const [loading,    setLoading]    = useState(true);
    const [activeType, setActiveType] = useState('all');
    const [settings,   setSettings]   = useState({});

    useEffect(() => {
        api.get('/admin/settings/public')
            .then(res => setSettings(res.data.settings || {}))
            .catch(() => {});
    }, []);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({ limit: 24 });
        if (activeType !== 'all') params.set('type', activeType);

        api.get(`/products?${params}`)
            .then(res => setProducts(res.data.products || []))
            .catch(() => setProducts([]))
            .finally(() => setLoading(false));
    }, [activeType]);

    const featured  = products.filter(p => p.is_featured);
    const rest      = products.filter(p => !p.is_featured);

    return (
        <>
            <Helmet>
                <title>Store — By Our Consent</title>
                <meta name="description"
                      content="Wear the movement. Every purchase helps spread the word that government serves the people." />
            </Helmet>

            <Nav />

            {/* Pre-launch heads-up banner -- informational only, never blocks anything.
                Toggle off from Admin > Settings once ready for real launch. */}
            {settings.prelaunch_banner_enabled === 'true' && (
                <div style={{
                    background:   '#5a3a1a',
                    color:        '#f5f0e8',
                    padding:      isMobile ? '10px 14px' : '10px 20px',
                    fontSize:     '12px',
                    fontFamily:   'sans-serif',
                    textAlign:    'center',
                    lineHeight:   1.5
                }}>
                    {settings.prelaunch_banner_text ||
                        "We're finishing up! Orders placed now are part of final testing."}
                </div>
            )}

            {/* Page header */}
            <div style={{
                background:   '#1e2a3a',
                padding:      isMobile ? '1rem' : '1.6rem 1.5rem',
                borderBottom: '2px solid #8b1a1a'
            }}>
                <div style={{ maxWidth: '960px', margin: '0 auto' }}>
                    <div style={{
                        fontSize:      '10px',
                        fontFamily:    'sans-serif',
                        letterSpacing: '0.18em',
                        textTransform: 'uppercase',
                        color:         '#8b6a3a',
                        marginBottom:  '4px'
                    }}>
                        The movement store
                    </div>
                    <h1 style={{
                        fontSize:     '32px',
                        fontWeight:   600,
                        color:        '#e8dfc8',
                        fontFamily:   "'EB Garamond', Georgia, serif",
                        marginBottom: '6px'
                    }}>
                        Wear the movement
                    </h1>
                    <p style={{
                        fontSize:   '14px',
                        fontStyle:  'italic',
                        color:      '#6a7a8a',
                        fontFamily: "'EB Garamond', Georgia, serif",
                        margin:     0
                    }}>
                        Every item purchased helps spread the word that government
                        serves the people — not the other way around.
                    </p>
                </div>
            </div>

            {/* Filter bar */}
            <div style={{
                background:   '#ede8dc',
                borderBottom: '0.5px solid #c8beaa',
                padding:      isMobile ? '10px 1rem' : '10px 1.5rem'
            }}>
                <div style={{
                    maxWidth:   '960px',
                    margin:     '0 auto',
                    display:    'flex',
                    alignItems: 'center',
                    gap:        '8px'
                }}>
                    <span style={{
                        fontSize:      '10px',
                        fontFamily:    'sans-serif',
                        color:         '#8a8070',
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase'
                    }}>
                        Filter
                    </span>
                    {[
                        { value: 'all',      label: 'All items'  },
                        { value: 'physical', label: 'Apparel'    },
                        { value: 'digital',  label: 'Digital'    }
                    ].map(({ value, label }) => (
                        <button
                            key={value}
                            onClick={() => setActiveType(value)}
                            style={{
                                background:    activeType === value ? '#1e2a3a' : 'transparent',
                                color:         activeType === value ? '#e8dfc8' : '#4a5060',
                                border:        `0.5px solid ${activeType === value ? '#1e2a3a' : '#c8beaa'}`,
                                padding:       '4px 14px',
                                fontSize:      '11px',
                                fontFamily:    'sans-serif',
                                borderRadius:  '20px',
                                cursor:        'pointer'
                            }}>
                            {label}
                        </button>
                    ))}

                </div>
            </div>

            {/* Products grid */}
            <div style={{
                maxWidth: '960px',
                margin:   '0 auto',
                padding:  isMobile ? '1rem' : '1.5rem'
            }}>
                {loading ? (
                    <div style={{
                        padding:    '4rem',
                        textAlign:  'center',
                        fontFamily: 'sans-serif',
                        color:      '#8a8070',
                        fontSize:   '14px'
                    }}>
                        Loading products...
                    </div>
                ) : products.length === 0 ? (
                    <div style={{
                        padding:    '4rem',
                        textAlign:  'center',
                        fontFamily: 'sans-serif',
                        color:      '#8a8070',
                        fontSize:   '14px'
                    }}>
                        No products found.
                    </div>
                ) : (
                    <>
                        {/* Featured products */}
                        {featured.length > 0 && (
                            <>
                                <div style={{
                                    fontSize:      '10px',
                                    fontFamily:    'sans-serif',
                                    letterSpacing: '0.14em',
                                    textTransform: 'uppercase',
                                    color:         '#8b6a3a',
                                    marginBottom:  '0.75rem'
                                }}>
                                    Featured
                                </div>
                                <div style={{
                                    display:             'grid',
                                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                    gap:                 '16px',
                                    marginBottom:        '2rem'
                                }}>
                                    {featured.map(p => (
                                        <ProductCard key={p.id} product={p} />
                                    ))}
                                </div>
                                {rest.length > 0 && (
                                    <div style={{
                                        height:       '0.5px',
                                        background:   '#c8beaa',
                                        marginBottom: '1.5rem'
                                    }} />
                                )}
                            </>
                        )}

                        {/* All other products */}
                        {rest.length > 0 && (
                            <>
                                {featured.length > 0 && (
                                    <div style={{
                                        fontSize:      '10px',
                                        fontFamily:    'sans-serif',
                                        letterSpacing: '0.14em',
                                        textTransform: 'uppercase',
                                        color:         '#8b6a3a',
                                        marginBottom:  '0.75rem'
                                    }}>
                                        All products
                                    </div>
                                )}
                                <div style={{
                                    display:             'grid',
                                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                    gap:                 '16px'
                                }}>
                                    {rest.map(p => (
                                        <ProductCard key={p.id} product={p} />
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            {/* Bottom banner */}
            <div style={{
                background:     '#ede8dc',
                borderTop:      '0.5px solid #c8beaa',
                borderBottom:   '0.5px solid #c8beaa',
                padding:        '1.25rem 1.5rem',
                textAlign:      'center',
                marginTop:      '1rem'
            }}>
                <div style={{
                    fontSize:   '14px',
                    fontFamily: 'sans-serif',
                    color:      '#5a6070',
                    lineHeight: 1.6
                }}>
                    All items fulfilled by Printful. Ships in 3–7 business days.
                    <br />
                    Questions? <Link to="/contact" style={{ color: '#8b1a1a' }}>Contact us</Link>
                    &nbsp;·&nbsp;
                    <Link to="/shipping" style={{ color: '#8b1a1a' }}>Shipping policy</Link>
                    &nbsp;·&nbsp;
                    <Link to="/returns" style={{ color: '#8b1a1a' }}>Returns</Link>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default StorePage;
